import InfoIcon from '../../../../../../ui/svg/common/info';
import React, { ChangeEvent, ClipboardEvent, KeyboardEvent, useCallback } from 'react';
import TaxIdInfoPopup from './taxid-info-popup';
import store, { billingAddressActions, rootActions, useAppDispatch, useAppSelector } from '../../../data/store';
import FormTextField from '../../../../../../ui/forms/fields/text-field';
import {
    removeInvisibleCharsOnPaste,
    updateControl,
    validateControl
} from '../../../../../../domain/validation/validation';
import { TaxIdStatusEnum } from '../../../enums/tax-id-status-enum';
import { getLocalData, getTaxAddressData } from '../../../domain/shopping-cart-address-provider';
import { getTax } from '../../../data/data-service';
import { debounce } from 'lodash';
import { FIELD_DEBOUNCE_DURATION } from '../../../domain/shopping-cart-provider';

/**
 *  TaxID is a widget that asks for tax / vat number - for all countries.
 *  If CMS_Country.IsTaxID = 1 ----> the widget is requited (for example, in Australia), otherwise it's optional.
 *  It should be a part of "edit address" form.
 *  https://wavesaudio.atlassian.net/browse/DSWEB-6
 */
const TaxID = () => {

    const dispatch = useAppDispatch();

    const taxIdPopupShown = useAppSelector(store => store.billingAddress.taxIdPopupShown);
    const taxIdControl = useAppSelector(store => store.billingAddress.taxIdControl);
    const taxIdSelectedStatusControl = useAppSelector(store => store.billingAddress.taxIdSelectedStatusControl);

    const countries = useAppSelector(store => store.root.countries);
    const countryIdControl = useAppSelector(store => store.billingAddress.countryIdControl);
    const selectedCountry = countries.find(country => country.countryID.toString() === countryIdControl.value.toString());
    const isTaxIdRequired = selectedCountry && selectedCountry.isTaxID;

    /**
     * open info popup
     */
    const taxIdInfoPopupInfoClicked = () => {
        dispatch(
            billingAddressActions.main({
                taxIdPopupShown: true,
            })
        );
    };

    /**
     * On input change
     */
    const handleChangeEvent = async (evt: ChangeEvent) => {
        const $target = evt.target as HTMLInputElement;

        dispatch(
            billingAddressActions.setControl({
                controlName: 'taxIdControl',
                control: updateControl(taxIdControl, $target.value, !taxIdControl.isValid)
            })
        );

        await debounced(taxIdSelectedStatusControl.value, $target.value);
    };

    /**
     * validate on blur
     */
    const handleBlurEvent = async () => {
        dispatch(
            billingAddressActions.setControl({
                controlName: 'taxIdControl',
                control: validateControl(taxIdControl),
            })
        );

        await debounced(taxIdSelectedStatusControl.value, taxIdControl.value);
    };

    /**
     * handle key up event
     */
    const handleKeyUp = (evt: KeyboardEvent) => {
        if(evt.key === 'Escape') {
            dispatch(
                billingAddressActions.setControl({
                    controlName: 'taxIdControl',
                    control: updateControl(taxIdControl, '', !taxIdControl.isValid)
                })
            );
        }
    };

    /**
     * remove invisible characters on paste event
     */
    const handlePasteEvent = async (evt: ClipboardEvent<HTMLInputElement>) => {
        const taxIdValue = removeInvisibleCharsOnPaste(evt);

        dispatch(
            billingAddressActions.setControl({
                controlName: 'taxIdControl',
                control: updateControl(taxIdControl, taxIdValue, !taxIdControl.isValid)
            })
        );

        await debounced(taxIdSelectedStatusControl.value, taxIdValue);
    };

    const changeTaxIdStatus = async (taxIdStatus: TaxIdStatusEnum) => {
        dispatch(
            billingAddressActions.setControl({
                controlName: 'taxIdSelectedStatusControl',
                control: updateControl(taxIdSelectedStatusControl,  taxIdStatus, !taxIdSelectedStatusControl.isValid)
            })
        );

        await debounced(taxIdStatus, taxIdControl.value);
    };

    // debounce is an external dependency, and it can't be replaced with inline function
    // eslint-disable-next-line
    const debounced = useCallback(
        debounce(
            (taxIdStatus: TaxIdStatusEnum, taxId: string) => updateCartDataOnTaxIdChange(taxIdStatus, taxId),FIELD_DEBOUNCE_DURATION
        ),
        []
    );

    /**
     * on taxId change ---> tax can be changed,
     * in such case ----> update the cart data
     */
    const updateCartDataOnTaxIdChange = async (taxIdStatus: TaxIdStatusEnum, taxId: string) => {

        const state = store.getState();
        const _countries = state.root.countries;
        const _billingCountryIdControl = state.billingAddress.countryIdControl;
        const _billingStateIdControl = state.billingAddress.stateIdControl;
        const _billingZipCodeControl = state.billingAddress.zipCodeControl;

        let taxAddressData = getTaxAddressData(_countries, _billingCountryIdControl.value, _billingStateIdControl.value, _billingZipCodeControl.value);
        if(!taxAddressData) return;

        const localData = getLocalData();
        localData.taxCountry2LetterCode = taxAddressData.taxCountry2LetterCode;
        localData.taxState2LetterCode = taxAddressData.taxState2LetterCode;
        localData.taxZipCode = taxAddressData.taxZipCode;
        localData.taxID = taxIdStatus === TaxIdStatusEnum.YES ? taxId : '';

        const response = await getTax(localData);

        if(!response || response.statusCode !== 200 || !response.data.isValid || !response.data.relatedData) return;

        dispatch(
            rootActions.setCartData(response.data.relatedData)
        );
    }

    return (
        <div className="mb-10" data-qa="cart-step3-taxid-section">
            <div className="flex items-center mb-1">
                <div className="font-bold text-lg text-gray-700 mr-2">Do you have a Tax / VAT number?</div>
                { !isTaxIdRequired && <span className="mr-2 text-sm text-gray-500">(Optional)</span> }
                <button
                    type="button"
                    className="text-sm text-gray-500 flex items-center"
                    onClick={ taxIdInfoPopupInfoClicked }>
                    <InfoIcon width="15px" color="#777" />
                </button>
            </div>

            { taxIdPopupShown && <TaxIdInfoPopup /> }

            <div className="text-lg text-gray-500 flex items-center">
                <label className="mr-4 flex items-center">
                    <input
                        type="radio"
                        name="tax-id-radio"
                        className="mr-1"
                        value={ TaxIdStatusEnum.YES }
                        checked={ taxIdSelectedStatusControl.value === TaxIdStatusEnum.YES }
                        onChange={ async () => {
                            await changeTaxIdStatus(TaxIdStatusEnum.YES);
                        }}
                    /> Yes </label>

                <label className="flex items-center">
                    <input
                        type="radio"
                        name="tax-id-radio"
                        className="mr-1"
                        data-qa="cart-step3-taxid-no-radio"
                        value={ TaxIdStatusEnum.NO }
                        checked={ taxIdSelectedStatusControl.value === TaxIdStatusEnum.NO }
                        onChange={ async () => {
                            await changeTaxIdStatus(TaxIdStatusEnum.NO);
                        }}
                    /> No </label>
            </div>

            {
                !taxIdSelectedStatusControl.isValid &&
                <div className="text-red text-sm">{ taxIdSelectedStatusControl.error }</div>
            }

            {
                taxIdSelectedStatusControl.value === TaxIdStatusEnum.YES &&
                <div className="mt-4">
                    <FormTextField
                        id={ 'taxId' }
                        label={ 'Tax / VAT number' }
                        maxLength={ selectedCountry && selectedCountry.taxIDMaxLength ? selectedCountry.taxIDMaxLength : 50 }
                        control={ taxIdControl }
                        textBoxClasses="rounded"
                        onChange={ handleChangeEvent }
                        onBlur={ handleBlurEvent }
                        onKeyUp={ handleKeyUp }
                        onPaste={ handlePasteEvent }
                    />
                </div>
            }

        </div>
    )
};

export default TaxID;
